import {
  ActionsMenu,
  PageContentLayout,
  PageHeaderLayout,
  Link,
  ListTotal,
  SideMenu,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { APP, ROUTES } from 'config';
import {
  COLOR_BY_STATUS,
  surveysFiltersSchema,
  surveyStatuses,
  useSurveys,
} from 'store/surveys';
import { useModalContext } from 'store/modal';
import { CreateSurveyModal, SurveyActions } from 'containers/surveys';
import { formatPercents } from 'utils/format';
import { useRouteMatch } from 'react-router-dom';
import { Divider, Stack } from '@mui/material';
import { useSearchParams } from 'utils/navigation';
import {
  DateRangeFilter,
  FilterButton,
  Filters,
  SelectFilter,
} from 'components/filters';
import { binaries } from 'types/Common';
import { useMe } from 'state/Administrators';

export const SurveysList = () => {
  const { t, i18n } = useTranslation();
  const { openModal } = useModalContext();
  const { path } = useRouteMatch();
  const { hasEditPermission } = useMe();
  const { searchParams: filters, navigate } =
    useSearchParams(surveysFiltersSchema);

  const { data: surveys, isLoading } = useSurveys({
    ...filters,
    type: 'survey',
  });

  return (
    <Stack gap={2}>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('surveys.surveys'),
          },
        ]}
      />

      <PageContentLayout
        sidebar={
          <SideMenu
            tabs={[
              {
                key: 'surveys',
                label: t('surveys.surveys'),
                url: ROUTES.surveys.survey.list,
                isActive: path === ROUTES.surveys.survey.list,
              },
            ]}
          />
        }
      >
        <TableWrapper>
          <TableToolbar>
            <Stack gap={1}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                gap={1}
              >
                <ActionsMenu
                  type="spread"
                  items={[
                    {
                      label: t('surveys.newSurvey'),
                      color: 'primary',
                      disabled: !hasEditPermission(['surveys']),
                      onClick: () => {
                        openModal({
                          component: CreateSurveyModal,
                          props: {
                            type: 'survey',
                            title: t('surveys.newSurvey'),
                          },
                        });
                      },
                    },
                  ]}
                />
              </Stack>

              <Divider />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
              >
                <ListTotal total={surveys?.total} />

                <Stack direction="row" alignItems="center" gap={2}>
                  <Filters
                    value={filters}
                    initialValues={surveysFiltersSchema.parse({})}
                    onChange={(v) => navigate({ params: { ...v, page: 1 } })}
                  >
                    {({ value: values, initialValues, setFilter }) => (
                      <>
                        <FilterButton
                          label={t('common.status')}
                          value={values.status}
                          initialValue={initialValues.status}
                          onChange={(v) => setFilter('status', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <SelectFilter
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                              multiple
                              options={surveyStatuses.map((status) => ({
                                key: status,
                                label: t(`status.${status}`),
                              }))}
                              searchable={false}
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('news.published_from')}
                          value={values.publishedAt}
                          initialValue={initialValues.publishedAt}
                          onChange={(v) => setFilter('publishedAt', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <DateRangeFilter
                              value={value}
                              onApply={applyFilter}
                              onClear={clearFilter}
                              onCancel={close}
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('surveys.anonymousSurvey')}
                          value={values.anonymous}
                          initialValue={initialValues.anonymous}
                          onChange={(v) => setFilter('anonymous', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <SelectFilter
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                              options={binaries.map((binary) => ({
                                key: binary,
                                label: t(`common.${binary}`),
                              }))}
                              searchable={false}
                              multiple={false}
                            />
                          )}
                        </FilterButton>
                      </>
                    )}
                  </Filters>
                </Stack>
              </Stack>
            </Stack>
          </TableToolbar>

          <Table
            data={surveys?.items ?? []}
            isLoading={isLoading}
            columns={[
              {
                id: 'name',
                header: t('surveys.systemName'),
                cell: ({ row: { original: survey } }) => {
                  return (
                    <Link
                      label={survey.name}
                      to={ROUTES.surveys[survey.type][
                        ['in_progress', 'ended'].includes(survey.status)
                          ? 'results'
                          : 'details'
                      ].replace(':id', survey.id)}
                    />
                  );
                },
                meta: { sort: true },
              },
              {
                id: 'status',
                header: t('common.status'),
                cell: ({ row: { original: survey } }) => {
                  return (
                    <Typography color={COLOR_BY_STATUS[survey.status]}>
                      {t(`surveys.status-${survey.status}`)}
                    </Typography>
                  );
                },
                meta: { sort: true },
              },
              {
                id: 'publishDate',
                header: t('surveys.publishDateTime'),
                cell: ({ row: { original: survey } }) => {
                  return survey.publishDateTime
                    ? moment(survey.publishDateTime).format('l LT')
                    : null;
                },
                meta: { sort: true },
              },
              {
                id: 'currentRecipients',
                header: t('common.recipients'),
                meta: { align: 'right' },
                cell: ({ row: { original: survey } }) => {
                  return survey.currentRecipients;
                },
              },
              {
                id: 'completedBy',
                header: t('surveys.completedBy'),
                meta: { align: 'right' },
                cell: ({ row: { original: survey } }) => {
                  return survey.completedBy;
                },
              },
              {
                id: 'responseRate',
                header: t('surveys.responseRate'),
                cell: ({ row: { original: survey } }) => {
                  return survey.currentRecipients
                    ? formatPercents(
                        i18n.language,
                        survey.completedBy / survey.currentRecipients,
                      )
                    : null;
                },
                meta: { align: 'right' },
              },
              {
                id: 'actions',
                cell: ({ row: { original: survey } }) => {
                  return <SurveyActions type="compact" survey={survey} />;
                },
              },
            ]}
            sort={{
              value: filters.sort,
              onChange: (v) => navigate({ params: { sort: v } }),
            }}
            pagination={
              surveys && {
                count: surveys.total,
                page: filters.page,
                pageSize: filters.pageSize,
                pageSizeOptions: APP.pagination.defaults.pageSize,
                onChange: (page, pageSize) => {
                  navigate({ params: { page, pageSize } });
                },
              }
            }
          />
        </TableWrapper>
      </PageContentLayout>
    </Stack>
  );
};
